// src/Redux/slices/visitorLogSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type VisitorLogEntry = any; // TODO: replace with your real type

export type PreEndOpenVisitor = {
  visitorId: string;
  lastInLogEntryId: string;
  lastInAtUtc: string;
  lookupDate?: string;
  description?: string;
};

export type PreEndCheckPayload = {
  siteId: string;
  associatedCompany: string;
  lookupDate: string;
  nextShiftStartIsoUtc: string;
  openVisitors: PreEndOpenVisitor[];
  openCount?: number;
  active?: boolean;
  createdAtIso?: string;
  updatedAtIso?: string;
  resolvedAtIso?: string;
};

export type VisitorLogSocketUpdateType =
  | 'VISITOR_LOGIN'
  | 'VISITOR_LOGOUT'
  | 'VISITOR_TEMP_OUT'
  | 'VISITOR_TEMP_IN'
  | 'VISITOR_DELETE'
  | 'SHIFT_ROLLOVER'
  | 'NDA_SIGNED';

export type VisitorLogSocketUpdatePayload = {
  type: VisitorLogSocketUpdateType;

  siteId?: string;
  site?: string; // compatibility fallback if backend sends "site"

  associatedCompany?: string;
  visitorId?: string;

  lookupDate?: string;
  prevLookupDate?: string;
  newLookupDate?: string;

  timestamp?: string;
  shiftStartIsoUtc?: string;

  previousStatus?: string;

  rolled?: number;
  tempOutFinalized?: number;
  gotAck?: boolean;
};

interface VisitorLogState {
  items: VisitorLogEntry[];

  preEndCheckBySiteId: Record<string, PreEndCheckPayload>;

  /**
   * Every visitorLog:update event bumps this number.
   *
   * Screens can subscribe to the selected site's version and refetch when
   * it changes. This avoids trying to reconstruct the full visitor record
   * from a small socket payload.
   */
  realtimeVersionBySiteId: Record<string, number>;
}

const initialState: VisitorLogState = {
  items: [],
  preEndCheckBySiteId: {},
  realtimeVersionBySiteId: {},
};

export const visitorLogSlice = createSlice({
  name: 'visitorLog',

  initialState,

  reducers: {
    setVisitorLog: (state, action: PayloadAction<VisitorLogEntry[]>) => {
      state.items = action.payload ?? [];
    },

    replaceOrAddVisitorLog: (state, action: PayloadAction<VisitorLogEntry>) => {
      const existingIndex = state.items.findIndex(
        (entry) => entry._id === action.payload._id
      );

      if (existingIndex !== -1) {
        state.items[existingIndex] = action.payload;
      } else {
        state.items.unshift(action.payload);
      }
    },

    updateVisitorLog: (state, action: PayloadAction<VisitorLogEntry>) => {
      const updated = action.payload;

      const index = state.items.findIndex(
        (visitor) => visitor._id === updated._id
      );

      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],

          logEntries: [
            ...(state.items[index].logEntries ?? []),
            ...(updated.logEntries ?? []),
          ],
        };
      }
    },

    prependVisitorLog: (state, action: PayloadAction<VisitorLogEntry>) => {
      state.items.unshift(action.payload);
    },

    clearVisitorLog: (state) => {
      state.items = [];
    },

    preEndCheckReceived: (state, action: PayloadAction<PreEndCheckPayload>) => {
      const payload = action.payload;

      if (payload.active !== false && payload.openVisitors?.length > 0) {
        state.preEndCheckBySiteId[payload.siteId] = payload;
      } else {
        delete state.preEndCheckBySiteId[payload.siteId];
      }
    },

    visitorLogSocketUpdateReceived: (
      state,
      action: PayloadAction<VisitorLogSocketUpdatePayload>
    ) => {
      const payload = action.payload;
      const siteId = payload.siteId || payload.site;

      if (!siteId) return;

      /**
       * All visitor movement changes, NDA changes, and shift rollover
       * cause the visitor screen to refetch from the backend.
       */
      state.realtimeVersionBySiteId[siteId] =
        (state.realtimeVersionBySiteId[siteId] || 0) + 1;

      /**
       * The pre-end warning is finished at the shift boundary.
       */
      if (payload.type === 'SHIFT_ROLLOVER') {
        delete state.preEndCheckBySiteId[siteId];
        return;
      }

      /**
       * A visitor who permanently leaves OR temporarily leaves is no
       * longer physically present, so remove them immediately from the
       * current pre-end alert.
       *
       * LogEntryService also emits a fresh preEndCheck payload while the
       * warning window is active. This local removal makes the UI respond
       * immediately and remains correct if that second event is delayed.
       */
      if (
        (payload.type === 'VISITOR_LOGOUT' ||
          payload.type === 'VISITOR_TEMP_OUT' ||
          payload.type === 'VISITOR_DELETE') &&
        payload.visitorId
      ) {
        const preEndCheck = state.preEndCheckBySiteId[siteId];

        if (preEndCheck) {
          preEndCheck.openVisitors = preEndCheck.openVisitors.filter(
            (visitor) => visitor.visitorId !== payload.visitorId
          );

          preEndCheck.openCount = preEndCheck.openVisitors.length;
          preEndCheck.active = preEndCheck.openVisitors.length > 0;

          if (preEndCheck.openVisitors.length === 0) {
            delete state.preEndCheckBySiteId[siteId];
          }
        }
      }

      /**
       * VISITOR_TEMP_IN and VISITOR_LOGIN are intentionally NOT manually
       * inserted into the warning payload here.
       *
       * The small visitorLog:update payload does not contain the full
       * last-in event metadata needed by PreEndOpenVisitor.
       *
       * During an active warning window the backend's LogEntryService
       * refreshes preEnd_active and emits visitorLog:preEndCheck, which
       * supplies the authoritative full list.
       */
    },

    clearPreEndCheckForSite: (state, action: PayloadAction<string>) => {
      delete state.preEndCheckBySiteId[action.payload];
    },
  },
});

export const {
  setVisitorLog,
  replaceOrAddVisitorLog,
  updateVisitorLog,
  prependVisitorLog,
  clearVisitorLog,
  preEndCheckReceived,
  visitorLogSocketUpdateReceived,
  clearPreEndCheckForSite,
} = visitorLogSlice.actions;

export default visitorLogSlice.reducer;

// Selectors
export const selectVisitorLog = (state: { visitorLog: VisitorLogState }) =>
  state.visitorLog.items;

export const selectPreEndCheckForSite = (
  state: { visitorLog: VisitorLogState },
  siteId?: string
) => {
  if (!siteId) return undefined;

  return state.visitorLog.preEndCheckBySiteId[siteId];
};

export const selectVisitorLogRealtimeVersion = (
  state: { visitorLog: VisitorLogState },
  siteId?: string
) => {
  if (!siteId) return 0;

  return state.visitorLog.realtimeVersionBySiteId[siteId] || 0;
};
